/** Docs (UA): see modules/game/DOCS.md — sprite pre-rendering (Espresso Brand art) */

import {
    GAME_PALETTE as P,
    GameTheme,
    rgbaFromHex as rgba,
    THEME_ITEM_COLORS,
} from '../game.constants';

export interface SpriteSet {
    item: HTMLCanvasElement;
    golden: HTMLCanvasElement;
    bug: HTMLCanvasElement;
    /** Same bug for cream surfaces (rules plate) — see renderBug(onLight). */
    bugLight: HTMLCanvasElement;
    mult: HTMLCanvasElement;
    catcher: HTMLCanvasElement;
    /** Visual center offsets so drawImage can rotate around the body center. */
    sizes: { item: number; golden: number; bug: number; mult: number };
}

const ITEM_SIZE = 48;
const GOLDEN_SIZE = 64;
const MULT_SIZE = 64;
/** Gold has the SAME body size as a plain item — it wins on colour, halo,
 *  spark and spin, not on scale. The bigger frame only holds the halo. */
const GOLDEN_SCALE = 1;
/** Body width of every catcher, in the 96-wide sprite frame. All themes share
 *  it: a 44px glass and a 76px tray made the same game measurably harder or
 *  easier depending on the machine. Mirrored by GAME_CONFIG.CUP.width.
 *  56 against a ~48 tall body reads as a vessel; 68 over the same height read
 *  as a flat basin. */
const CATCHER_BODY = 56;

const mkCanvas = (w: number, h: number, dpr: number): [HTMLCanvasElement, CanvasRenderingContext2D] => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Game sprites: 2d context unavailable');
    ctx.scale(dpr, dpr);
    return [canvas, ctx];
};

const drawGloss = (ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number): void => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((-30 * Math.PI) / 180);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
};

/** Radial fill with the LIGHT stop dominant (0–70% of radius) — keeps the
 *  body bright against the dark sky; only the rim darkens. */
const domeGradient = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, light: string, deep: string): CanvasGradient => {
    const g = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.25, r * 0.1, cx, cy, r);
    g.addColorStop(0, light);
    g.addColorStop(0.7, light);
    g.addColorStop(1, deep);
    return g;
};

/** Popcorn lobes: four overlapping circles. Stroking each one would leave
 *  internal seams, so the outline is a fatter ink pass drawn underneath. */
const POPCORN_LOBES: Array<[number, number, number]> = [
    [24, 27, 11.5],
    [14.5, 20, 8.5],
    [33.5, 20.5, 8.5],
    [24, 12.5, 8],
];

const lobePath = (ctx: CanvasRenderingContext2D, grow: number): void => {
    ctx.beginPath();
    for (const [cx, cy, r] of POPCORN_LOBES) {
        ctx.moveTo(cx + r + grow, cy);
        ctx.arc(cx, cy, r + grow, 0, Math.PI * 2);
    }
};

/** Coffee bean: tilted oval with an S-seam — the silhouette Vadym asked for. */
const beanBody = (ctx: CanvasRenderingContext2D): void => {
    ctx.beginPath();
    ctx.ellipse(24, 24, 14.5, 18.5, 0, 0, Math.PI * 2);
};

const beanSeam = (ctx: CanvasRenderingContext2D): void => {
    ctx.beginPath();
    ctx.moveTo(24, 7.5);
    ctx.bezierCurveTo(18, 15, 30, 23, 24, 40.5);
};

/** Sealed snack pack: serrated crimp on the top and bottom edges. Its visual
 *  mass matches the bean and the popcorn — a smaller pack reads as farther away. */
const packBody = (ctx: CanvasRenderingContext2D): void => {
    const teeth = [9, 13, 17, 21, 25, 29, 33, 37, 39];
    ctx.beginPath();
    ctx.moveTo(teeth[0], 12);
    teeth.slice(1).forEach((x, i) => ctx.lineTo(x, i % 2 === 0 ? 7 : 12));
    ctx.lineTo(39, 36);
    [...teeth].reverse().slice(1).forEach((x, i) => ctx.lineTo(x, i % 2 === 0 ? 41 : 36));
    ctx.closePath();
};

const dropBody = (ctx: CanvasRenderingContext2D): void => {
    ctx.beginPath();
    ctx.moveTo(24, 5);
    ctx.bezierCurveTo(28, 13, 38, 21, 38, 30);
    ctx.arc(24, 30, 14, 0, Math.PI, false);
    ctx.bezierCurveTo(10, 21, 20, 13, 24, 5);
    ctx.closePath();
};

const renderItem = (theme: GameTheme, dpr: number): HTMLCanvasElement => {
    const [canvas, ctx] = mkCanvas(ITEM_SIZE, ITEM_SIZE, dpr);
    const { light, deep } = THEME_ITEM_COLORS[theme];
    ctx.lineWidth = 3;
    ctx.strokeStyle = P.ink;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    if (theme === 'popcorn') {
        ctx.fillStyle = P.ink;
        lobePath(ctx, 1.5);
        ctx.fill();
        ctx.fillStyle = domeGradient(ctx, 24, 22, 20, light, deep);
        lobePath(ctx, 0);
        ctx.fill();
        ctx.fillStyle = deep;
        ctx.beginPath();
        ctx.ellipse(24, 29.5, 3.5, 2.5, 0, 0, Math.PI * 2);
        ctx.fill();
        drawGloss(ctx, 17.5, 16.5, 4.5, 2.8);
        return canvas;
    }

    if (theme === 'water') {
        dropBody(ctx);
        ctx.fillStyle = domeGradient(ctx, 24, 26, 17, light, deep);
        ctx.fill();
        ctx.stroke();
        drawGloss(ctx, 17, 24, 4.5, 2.8);
        return canvas;
    }

    if (theme === 'snack') {
        packBody(ctx);
        ctx.fillStyle = domeGradient(ctx, 24, 22, 18, light, deep);
        ctx.fill();
        ctx.stroke();
        ctx.strokeStyle = deep;
        ctx.globalAlpha = 0.7;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(17, 19);
        ctx.lineTo(31, 19);
        ctx.stroke();
        ctx.globalAlpha = 1;
        drawGloss(ctx, 18, 16, 4.5, 2.8);
        return canvas;
    }

    // Coffee bean — the fleet default (~92% of machines).
    ctx.save();
    ctx.translate(24, 24);
    ctx.rotate((-14 * Math.PI) / 180);
    ctx.translate(-24, -24);
    beanBody(ctx);
    ctx.fillStyle = domeGradient(ctx, 24, 22, 19, light, deep);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = deep;
    ctx.lineWidth = 3;
    beanSeam(ctx);
    ctx.stroke();
    ctx.strokeStyle = rgba(P.ink, 0.35);
    ctx.lineWidth = 1.2;
    beanSeam(ctx);
    ctx.stroke();
    ctx.restore();
    drawGloss(ctx, 17, 15, 5, 3);
    return canvas;
};

const goldFill = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): CanvasGradient => {
    const g = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.3, r * 0.1, cx, cy, r);
    g.addColorStop(0, '#FFFFFF');
    g.addColorStop(0.35, P.goldGlow);
    g.addColorStop(1, P.gold);
    return g;
};

const drawSpark = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void => {
    ctx.fillStyle = P.cream;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r * 0.28, cy - r * 0.28);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx + r * 0.28, cy + r * 0.28);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r * 0.28, cy + r * 0.28);
    ctx.lineTo(cx - r, cy);
    ctx.lineTo(cx - r * 0.28, cy - r * 0.28);
    ctx.closePath();
    ctx.fill();
};

/** Golden item inside its pre-rendered halo (single sprite, single drawImage).
 *  Same silhouette as the regular item, so colour + halo + spark + scale carry
 *  the whole difference (design spec §3.8 asks for three independent channels). */
const renderGoldenItem = (theme: GameTheme, dpr: number): HTMLCanvasElement => {
    const S = GOLDEN_SIZE;
    const c = S / 2;
    const [canvas, ctx] = mkCanvas(S, S, dpr);
    const halo = ctx.createRadialGradient(c, c, 4, c, c, c);
    halo.addColorStop(0, rgba(P.goldGlow, 0.55));
    halo.addColorStop(1, rgba(P.goldGlow, 0));
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, S, S);

    ctx.save();
    ctx.translate(c, c);
    ctx.scale(GOLDEN_SCALE, GOLDEN_SCALE);
    if (theme === 'coffee') ctx.rotate((-14 * Math.PI) / 180);
    ctx.translate(-24, -24);
    ctx.strokeStyle = P.goldStroke;
    ctx.lineWidth = 2.4 / GOLDEN_SCALE;
    ctx.lineJoin = 'round';
    if (theme === 'popcorn') {
        // Stroking the lobes would draw every circle: outline as a fatter fill.
        ctx.fillStyle = P.goldStroke;
        lobePath(ctx, 2.4 / GOLDEN_SCALE);
        ctx.fill();
        ctx.fillStyle = goldFill(ctx, 24, 22, 20);
        lobePath(ctx, 0);
        ctx.fill();
    } else {
        ctx.fillStyle = goldFill(ctx, 24, 22, 20);
        if (theme === 'water') dropBody(ctx);
        else if (theme === 'snack') packBody(ctx);
        else beanBody(ctx);
        ctx.fill();
        ctx.stroke();
    }
    if (theme === 'coffee') {
        ctx.globalAlpha = 0.75;
        ctx.lineWidth = 2.2 / GOLDEN_SCALE;
        beanSeam(ctx);
        ctx.stroke();
        ctx.globalAlpha = 1;
    }
    ctx.restore();
    drawSpark(ctx, S - 19, 25, 9);
    return canvas;
};

/** `onLight` flips the limbs and the rim to ink: the bug is drawn for the dark
 *  field, and on the cream rule plate its cream legs and rim simply vanish.
 *  The chevrons and eyes stay cream — they sit on the dark body either way. */
const renderBug = (dpr: number, onLight = false): HTMLCanvasElement => {
    const S = 48;
    const [canvas, ctx] = mkCanvas(S, S, dpr);
    const cream = (alpha: number): string => rgba(P.cream, alpha);
    const limb = (alpha: number): string => (onLight ? rgba(P.ink, alpha) : cream(alpha));

    // Legs + antennae — light limbs on the field, ink limbs on light surfaces.
    ctx.strokeStyle = limb(0.8);
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    const legs: Array<[number, number, number, number, number, number]> = [
        [13, 20, 6, 18, 4, 12],
        [12, 28, 4, 28, 2, 23],
        [13, 35, 7, 38, 6, 44],
        [35, 20, 42, 18, 44, 12],
        [36, 28, 44, 28, 46, 23],
        [35, 35, 41, 38, 42, 44],
    ];
    for (const [x1, y1, qx, qy, x2, y2] of legs) {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(qx, qy, x2, y2);
        ctx.stroke();
    }
    ctx.lineWidth = 2;
    for (const dir of [-1, 1] as const) {
        ctx.beginPath();
        ctx.moveTo(24 + dir * 4, 11);
        ctx.quadraticCurveTo(24 + dir * 7, 6, 24 + dir * 10, 5);
        ctx.stroke();
        ctx.fillStyle = limb(1);
        ctx.beginPath();
        ctx.arc(24 + dir * 10.5, 4.5, 1.8, 0, Math.PI * 2);
        ctx.fill();
    }

    // Rim OUTSIDE the dark body — silhouette survives protanopia and any
    // backdrop (dark body would vanish without it); on light surfaces it only
    // needs to add weight, so it goes ink and faint.
    ctx.strokeStyle = onLight ? rgba(P.ink, 0.25) : cream(0.9);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(24, 27, 13.5, 16.5, 0, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = P.bugBody;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(24, 27, 11, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(24, 14);
    ctx.lineTo(24, 40);
    ctx.stroke();

    // Danger chevrons, each cream-outlined.
    for (const y of [21, 30]) {
        for (const [color, width] of [[cream(1), 6.5], [P.danger, 3.5]] as const) {
            ctx.strokeStyle = color;
            ctx.lineWidth = width;
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(18, y);
            ctx.lineTo(24, y + 4.5);
            ctx.lineTo(30, y);
            ctx.stroke();
        }
    }
    ctx.fillStyle = cream(1);
    for (const dir of [-1, 1] as const) {
        ctx.beginPath();
        ctx.arc(24 + dir * 3.5, 16.5, 1.8, 0, Math.PI * 2);
        ctx.fill();
    }
    return canvas;
};

const renderMult = (dpr: number): HTMLCanvasElement => {
    const S = MULT_SIZE;
    const [canvas, ctx] = mkCanvas(S, S, dpr);
    const halo = ctx.createRadialGradient(32, 28, 2, 32, 28, 32);
    halo.addColorStop(0, rgba(P.accent, 0.35));
    halo.addColorStop(1, rgba(P.accent, 0));
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, S, S);

    // Stick.
    ctx.fillStyle = P.cream;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(29, 45, 6, 14, 3);
    ctx.fill();
    ctx.stroke();

    // 8 alternating wedges, 45° each — stripes ≥9px at the rim: zero moiré.
    const cx = 32;
    const cy = 28;
    const R = 20;
    for (let i = 0; i < 8; i += 1) {
        const a1 = ((i * 45 - 90) * Math.PI) / 180;
        const a2 = (((i + 1) * 45 - 90) * Math.PI) / 180;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R, a1, a2);
        ctx.closePath();
        ctx.fillStyle = i % 2 === 0 ? P.accent : P.cream;
        ctx.fill();
    }
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.stroke();
    // Center plate; the ×2 label is drawn UPRIGHT by the renderer while the
    // disc sprite rotates — text that spins is unreadable.
    ctx.fillStyle = P.cream;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return canvas;
};

/** Tapered vessel shared by cup, bucket and tray — only the numbers differ. */
const vessel = (
    ctx: CanvasRenderingContext2D,
    top: number,
    topY: number,
    bottom: number,
    bottomY: number,
): void => {
    const l = 48 - top / 2;
    const r = 48 + top / 2;
    const bl = 48 - bottom / 2;
    const br = 48 + bottom / 2;
    ctx.beginPath();
    ctx.moveTo(l, topY);
    ctx.lineTo(r, topY);
    ctx.lineTo(br, bottomY - 5);
    ctx.quadraticCurveTo(br - 0.5, bottomY, br - 7, bottomY);
    ctx.lineTo(bl + 7, bottomY);
    ctx.quadraticCurveTo(bl + 0.5, bottomY, bl, bottomY - 5);
    ctx.closePath();
};

const catcherCoffee = (ctx: CanvasRenderingContext2D): void => {
    ctx.fillStyle = P.cream;
    vessel(ctx, CATCHER_BODY, 12, 44, 60);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = P.accent;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(21.5, 26);
    ctx.lineTo(74.5, 26);
    ctx.lineTo(73, 38);
    ctx.lineTo(23, 38);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = P.cream;
    ctx.beginPath();
    ctx.moveTo(48, 27.5);
    ctx.lineTo(52.5, 32);
    ctx.lineTo(48, 36.5);
    ctx.lineTo(43.5, 32);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = P.uiText;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(48, 12, CATCHER_BODY / 2, 5.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
};

/** Popcorn tub: striped bucket with kernels spilling over the rim. */
const catcherPopcorn = (ctx: CanvasRenderingContext2D): void => {
    ctx.fillStyle = P.cream;
    vessel(ctx, CATCHER_BODY, 10, 44, 60);
    ctx.fill();
    ctx.stroke();
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = P.accent;
    ctx.lineWidth = 8;
    for (const [x1, x2] of [[27, 32], [38, 40], [52, 50], [64, 60]]) {
        ctx.beginPath();
        ctx.moveTo(x1, 11);
        ctx.lineTo(x2, 59);
        ctx.stroke();
    }
    ctx.restore();
    ctx.fillStyle = P.uiText;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(48, 10, CATCHER_BODY / 2, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = P.cream;
    ctx.lineWidth = 2.5;
    for (const [cx, cy, r] of [[38, 6, 6.5], [58, 6, 6], [48, 2.5, 7]] as const) {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
    }
};

/** Snack tray: wide and flat, so its silhouette never reads as the cup. */
const catcherSnack = (ctx: CanvasRenderingContext2D): void => {
    ctx.fillStyle = P.accentAlt;
    vessel(ctx, CATCHER_BODY, 22, 48, 60);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = P.cream;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(19, 14);
    ctx.lineTo(77, 14);
    ctx.lineTo(75.5, 25);
    ctx.lineTo(20.5, 25);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = P.uiText;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(48, 14, CATCHER_BODY / 2, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
};

const catcherWater = (ctx: CanvasRenderingContext2D): void => {
    ctx.fillStyle = rgba(THEME_ITEM_COLORS.water.deep, 0.45);
    ctx.strokeStyle = rgba(P.cream, 0.85);
    ctx.lineWidth = 3;
    vessel(ctx, CATCHER_BODY, 8, 46, 60);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'rgba(47,182,206,0.7)';
    ctx.beginPath();
    ctx.moveTo(23, 30);
    ctx.lineTo(73, 29);
    ctx.lineTo(70.5, 55);
    ctx.quadraticCurveTo(70, 59, 65, 59);
    ctx.lineTo(31, 59);
    ctx.quadraticCurveTo(26, 59, 25.5, 55);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(27, 13);
    ctx.lineTo(26, 50);
    ctx.moveTo(69, 13);
    ctx.lineTo(69, 50);
    ctx.stroke();
    ctx.fillStyle = '#1E5C6B';
    ctx.strokeStyle = rgba(P.cream, 0.85);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(48, 8, CATCHER_BODY / 2, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Citrus slice.
    ctx.fillStyle = P.gold;
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(72, 8, 8.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = P.cream;
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 4; i += 1) {
        const a = (i * Math.PI) / 4;
        ctx.beginPath();
        ctx.moveTo(72 - 6.5 * Math.cos(a), 8 - 6.5 * Math.sin(a));
        ctx.lineTo(72 + 6.5 * Math.cos(a), 8 + 6.5 * Math.sin(a));
        ctx.stroke();
    }
};

const CATCHERS: Record<GameTheme, (ctx: CanvasRenderingContext2D) => void> = {
    coffee: catcherCoffee,
    popcorn: catcherPopcorn,
    snack: catcherSnack,
    water: catcherWater,
};

const renderCatcher = (theme: GameTheme, dpr: number): HTMLCanvasElement => {
    const [canvas, ctx] = mkCanvas(96, 64, dpr);
    ctx.lineJoin = 'round';
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 3.5;
    CATCHERS[theme](ctx);
    return canvas;
};

/** Pre-render everything once at init — createRadialGradient/shadowBlur in
 *  the frame loop are forbidden by the art spec (60 fps on cheap Android). */
export const createSprites = (theme: GameTheme, dpr: number): SpriteSet => ({
    item: renderItem(theme, dpr),
    golden: renderGoldenItem(theme, dpr),
    bug: renderBug(dpr),
    bugLight: renderBug(dpr, true),
    mult: renderMult(dpr),
    catcher: renderCatcher(theme, dpr),
    sizes: { item: ITEM_SIZE, golden: GOLDEN_SIZE, bug: 48, mult: MULT_SIZE },
});
